import { Injectable, effect, signal } from '@angular/core';
import { Subject } from 'rxjs';

export interface AppConfig {
    inputStyle: string;
    colorScheme: string;
    theme: string;
    ripple: boolean;
    menuMode: string;
    scale: number;
}

interface LayoutState {
    staticMenuDesktopInactive: boolean;
    overlayMenuActive: boolean;
    profileSidebarVisible: boolean;
    configSidebarVisible: boolean;
    staticMenuMobileActive: boolean;
    menuHoverActive: boolean;
    /** Escritorio: menú angosto, solo íconos. Se recuerda entre visitas. */
    rail: boolean;
}

const RAIL_KEY = 'dh.sidebarRail';
const THEME_KEY = 'dh.theme';

/** Colores del panel. "cafe" = marca Drink Hopping; "negro" y "blanco" = blanco y negro del logo del admin. */
export type DhColorTheme = 'cafe' | 'negro' | 'blanco';
export const DH_COLOR_THEMES: { id: DhColorTheme; label: string }[] = [
    { id: 'cafe', label: 'Café y naranja' },
    { id: 'negro', label: 'Negro (menú oscuro)' },
    { id: 'blanco', label: 'Blanco (menú claro)' },
];
function readRail(): boolean {
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
}

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    _config: AppConfig = {
        ripple: false,
        inputStyle: 'outlined',
        menuMode: 'static',
        colorScheme: 'light',
        theme: 'drinkhopping-light',
        scale: 14,
    };

    config = signal<AppConfig>(this._config);

    state: LayoutState = {
        staticMenuDesktopInactive: false,
        overlayMenuActive: false,
        profileSidebarVisible: false,
        configSidebarVisible: false,
        staticMenuMobileActive: false,
        menuHoverActive: false,
        rail: readRail(),
    };

    colorTheme: DhColorTheme = this.readTheme();

    private configUpdate = new Subject<AppConfig>();

    private overlayOpen = new Subject<any>();

    configUpdate$ = this.configUpdate.asObservable();

    overlayOpen$ = this.overlayOpen.asObservable();

    constructor() {
        effect(() => {
            const config = this.config();
            if (this.updateStyle(config)) {
                this.changeTheme();
            }
            this.changeScale(config.scale);
            this.onConfigUpdate();
        });
    }

    updateStyle(config: AppConfig) {
        return (
            config.theme !== this._config.theme ||
            config.colorScheme !== this._config.colorScheme
        );
    }

    onMenuToggle() {
        if (this.isOverlay()) {
            this.state.overlayMenuActive = !this.state.overlayMenuActive;
            if (this.state.overlayMenuActive) {
                this.overlayOpen.next(null);
            }
        }

        if (this.isDesktop()) {
            // En escritorio el menú no se esconde: se achica a una columna de íconos.
            this.state.rail = !this.state.rail;
            try { localStorage.setItem(RAIL_KEY, this.state.rail ? '1' : '0'); } catch { /* sin almacenamiento */ }
        } else {
            this.state.staticMenuMobileActive =
                !this.state.staticMenuMobileActive;

            if (this.state.staticMenuMobileActive) {
                this.overlayOpen.next(null);
            }
        }
    }

    setColorTheme(theme: DhColorTheme) {
        this.colorTheme = theme;
        if (theme === 'cafe') document.documentElement.removeAttribute('data-dh-theme');
        else document.documentElement.setAttribute('data-dh-theme', theme);
        try { localStorage.setItem(THEME_KEY, theme); } catch { /* sin almacenamiento */ }
    }

    private readTheme(): DhColorTheme {
        try {
            const t = localStorage.getItem(THEME_KEY) as DhColorTheme | null;
            return DH_COLOR_THEMES.some((x) => x.id === t) ? (t as DhColorTheme) : 'cafe';
        } catch { return 'cafe'; }
    }

    showProfileSidebar() {
        this.state.profileSidebarVisible = !this.state.profileSidebarVisible;
        if (this.state.profileSidebarVisible) {
            this.overlayOpen.next(null);
        }
    }

    showConfigSidebar() {
        this.state.configSidebarVisible = true;
    }

    isOverlay() {
        return this.config().menuMode === 'overlay';
    }

    isDesktop() {
        return window.innerWidth > 991;
    }

    isMobile() {
        return !this.isDesktop();
    }

    onConfigUpdate() {
        this._config = { ...this.config() };
        this.configUpdate.next(this.config());
    }

    changeTheme() {
        const config = this.config();
        const themeLink = <HTMLLinkElement>document.getElementById('theme-css');
        const themeLinkHref = themeLink.getAttribute('href')!;
        const newHref = themeLinkHref
            .split('/')
            .map((el) =>
                el == this._config.theme
                    ? (el = config.theme)
                    : el == `theme-${this._config.colorScheme}`
                    ? (el = `theme-${config.colorScheme}`)
                    : el
            )
            .join('/');

        this.replaceThemeLink(newHref);
    }
    replaceThemeLink(href: string) {
        const id = 'theme-css';
        let themeLink = <HTMLLinkElement>document.getElementById(id);
        const cloneLinkElement = <HTMLLinkElement>themeLink.cloneNode(true);

        cloneLinkElement.setAttribute('href', href);
        cloneLinkElement.setAttribute('id', id + '-clone');

        themeLink.parentNode!.insertBefore(
            cloneLinkElement,
            themeLink.nextSibling
        );
        cloneLinkElement.addEventListener('load', () => {
            themeLink.remove();
            cloneLinkElement.setAttribute('id', id);
        });
    }

    changeScale(value: number) {
        document.documentElement.style.fontSize = `${value}px`;
    }
}
